import { atom, useAtom } from "jotai";

import { useUserScopes } from "#/services/dynamic/hooks/useUserScopes";
import { type RFQResponseState } from "#/services/kyan/kyan.types";
import { isEqualAddress } from "#/utils/isEqualAddress";

import { usePositionsDrawerMarket } from "../app/positionsDrawer/positionsDrawerMarket";
import { useAccount } from "../wallet/account/account";

const rfqResponsesAtom = atom<RFQResponseState[]>([]);

const isSingleLeg = (response: RFQResponseState) => response.totalLegs === 1;
const isMultiLeg = (response: RFQResponseState) => response.totalLegs > 1;
const isOptionRfq = (response: RFQResponseState) =>
  response.instrumentType === "options";
export const isPerpsRfq = (response: RFQResponseState) =>
  response.instrumentType === "perps";

export const useRfqResponses = () => {
  const { market } = usePositionsDrawerMarket();
  const [rfqResponses, setRfqResponses] = useAtom(rfqResponsesAtom);
  const { account } = useAccount();
  const { hasEarlyAccessVip } = useUserScopes();

  const filteredResponses =
    !hasEarlyAccessVip || !account
      ? rfqResponses.filter((response) =>
          isEqualAddress(response.taker, account),
        )
      : rfqResponses.filter(
          (response) =>
            isEqualAddress(response.taker, account) ||
            isEqualAddress(response.maker, account),
        );

  const responses = filteredResponses.sort((a, b) =>
    (a.legs[0]?.instrumentName ?? "").localeCompare(
      b.legs[0]?.instrumentName ?? "",
    ),
  );

  const optionsResponses = responses.filter(isOptionRfq);
  const perpsResponses = responses.filter(isPerpsRfq);

  const marketResponses = responses.filter((response) =>
    response.marketPairs.includes(market.pair),
  );

  return {
    setRfqResponses,
    rfqResponses: responses,
    marketResponses,
    optionsResponses: {
      singleLeg: optionsResponses.filter(isSingleLeg),
      multiLeg: optionsResponses.filter(isMultiLeg),
    },
    perpsResponses: {
      singleLeg: perpsResponses.filter(isSingleLeg),
      multiLeg: perpsResponses.filter(isMultiLeg),
    },
    hasAnyRfqResponse: responses.length > 0,
  };
};
